<script setup lang="ts">
import type { Property } from '@/core/entities/property';
import type { ChosenPropertiesProps } from '.';

import { OffMarketCard } from '@/components/OffMarketCard';
import { PropertyCard } from '@/components/PropertyCard';
import { Skeleton } from '~/components/ui/skeleton';

import { cn } from '@/utils';
import { breakpointsTailwind } from '@vueuse/core';

const props = withDefaults(defineProps<ChosenPropertiesProps>(), {
	properties: () => [],
	offMarketCardIndex: 2,
	containerClass: '',
	mainContainerClass: '',
	isLoading: false,
	titleClass: 'text-[28px] md:text-[40px]'
});

const emit = defineEmits<{
	(
		e: 'cta_view_more_clicked',
		payload: { href: string; clickCount: number; ctaText: string }
	): void;
}>();

const clickCount = ref(0);

const cardsToRender = computed(() => {
	const mappedProperties = props.properties.map(property => ({
		isProperty: true,
		data: property
	})) as { isProperty: boolean; data: Property | null }[];

	mappedProperties.splice(props.offMarketCardIndex, 0, {
		isProperty: false,
		data: null
	});

	return mappedProperties;
});

const breakpoints = useBreakpoints(breakpointsTailwind);

function handleViewMoreClicked(navigate: () => void, href: string) {
	clickCount.value += 1;
	const ctaText = breakpoints.greaterOrEqual('md')
		? 'Ver todos'
		: 'Ver todos os imóveis';

	emit('cta_view_more_clicked', {
		href,
		clickCount: clickCount.value,
		ctaText
	});
	navigate();
}
</script>

<template>
	<section class="relative w-full bg-white">
		<Container :class="containerClass" :main-class="mainContainerClass">
			<header class="flex flex-col items-start pb-9">
				<Skeleton
					:is-active="isLoading"
					variant="white"
					:class="isLoading ? 'h-[34px] w-1/2 md:w-[359px]' : ''"
				>
					<h2
						:class="
							cn(
								'flex items-center font-matter-sq font-normal text-primary',
								titleClass
							)
						"
					>
						Imóveis selecionados para você
					</h2>
				</Skeleton>
			</header>
			<div
				class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-2 xl:grid-cols-3"
			>
				<template v-if="isLoading">
					<PropertyCardSkeleton
						v-for="i in 6"
						:key="i"
						variant="whiteBackground"
					/>
				</template>

				<template v-else>
					<template v-for="(item, index) in cardsToRender" :key="index">
						<NuxtLink
							v-if="item.isProperty && item.data"
							:to="`/imovel/${item.data.commercialId}/${item.data.slug}`"
							target="_blank"
							rel="noopener noreferrer"
							:data-test-id="`property-card-link-${index}`"
						>
							<PropertyCard
								class="min-h-fit md:max-h-[380px]"
								:property="item.data"
								section="selected properties"
								:card-position="index"
							/>
						</NuxtLink>

						<OffMarketCard
							v-else-if="!item.isProperty"
							class="min-h-fit max-w-fit md:max-h-[380px]"
						/>
					</template>
				</template>
			</div>
			<div class="mt-6">
				<NuxtLink v-slot="{ href, navigate }" custom :to="ctaLink">
					<Button
						as="a"
						size="md"
						class="w-[231px] text-base font-medium md:w-[172px] md:text-lg"
						:href="href"
						:is-skeleton-loading="isLoading"
						skeleton-classes="w-[231px] md:w-[172px] h-[52px]"
						skeleton-variant="white"
						@click="handleViewMoreClicked(navigate, href)"
					>
						<span class="block md:hidden">Ver todos os imóveis</span>
						<span class="hidden md:block">Ver todos</span>
						<IconMaterialSymbolsLightNorthEast class="size-5" />
					</Button>
				</NuxtLink>
			</div>
		</Container>
	</section>
</template>
